import React, { type ReactElement, useEffect, useState } from 'react'
import Dialog, { type DialogProps } from '@mui/material/Dialog'
import { Box, Typography, useTheme } from '@mui/material'
import { format } from 'date-fns'
import projectDetailsBackground from 'assets/projectDetailsBackground.svg'
import { ReactComponent as DownloadIcon } from 'assets/downloadIcon.svg'
import { BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import { GeneratedFileType } from 'common/files/files.types'
import { downloadFile, downloadHTML, getFileUrl } from 'common/files/files.service'
import { getTopicColor, getTopicIcon, getTopicLabel } from 'common/tenders/tenders.functions'
import ThemeButton from 'common/ui/theme-button'
import IconButton from 'common/ui/icon-button'
import SimpleTooltip from 'common/ui/simple-tooltip'
import { setErrorMessage } from 'store/app.slice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import parse from 'html-react-parser'

import { type Bid } from '../bid.types'

import styles from './BidDetailsDialog.module.scss'

interface BidDetailsDialogProps extends DialogProps {
  open: boolean
  bid: Bid
  confirmButtonAction: () => void
  declineButtonAction: () => void
  onClose: () => void
}
const BidDetailsDialog: React.FC<BidDetailsDialogProps> = ({
  open,
  bid,
  onClose,
  confirmButtonAction,
  declineButtonAction,
  ...rest
}): ReactElement => {
  const { palette } = useTheme()
  const [brief, setBriefContent] = useState<string>('')
  const dispatch = useAppDispatch()
  const { vendor } = useAppSelector((state) => state.vendor)

  useEffect(() => {
    void handleFetchDescription()
  }, [])

  const handleDownloadBrief = async (): Promise<void> => {
    if (bid.brief) {
      await downloadFile(GeneratedFileType.ProjectDescriptionDoc, bid.brief, {
        filename: bid.brief.filename,
        key: bid.brief.key,
      })
    }
  }

  const handleFetchDescription = async (): Promise<void> => {
    if (bid.brief) {
      const generatedFile = await getFileUrl({
        key: bid.brief?.key,
        mode: 'r',
      })
      if (generatedFile?.read?.url) {
        const downloadedContent = await downloadHTML(generatedFile.read.url)
        setBriefContent(downloadedContent ?? '')
      }
    }
  }

  return (
    <Dialog open={open} onClose={onClose} {...rest}>
      <Box className={styles.container}>
        <div className={styles.infoWrapper}>
          <div className={styles.name}>
            <Typography variant="description3">{bid.name ?? ''}</Typography>
          </div>
          <div className={styles.buttons}>
            <SimpleTooltip title={vendor?.contact ? '' : 'Morate dodati kontakt osobu na profilu.'}>
              <ThemeButton
                onClick={() => {
                  confirmButtonAction()
                }}
                sx={{ width: '264px' }}
                disabled={!vendor?.contact}
              >
                PRIHVATI
              </ThemeButton>
            </SimpleTooltip>
            <ThemeButton
              onClick={() => {
                declineButtonAction()
              }}
              variant={BUTTON_VARIANTS.secondary}
              sx={{ width: '264px' }}
            >
              ODBIJ
            </ThemeButton>
          </div>
        </div>
        <div className={styles.infoWrapper}>
          <div className={styles.description}>
            <div className={styles.descriptionTitle}>
              <Typography variant="title1">Opis projekta</Typography>
            </div>
            <div className={styles.border} />
            <div className={styles.descriptionText}>{parse(brief)}</div>
          </div>
          <div className={styles.briefDeadlineWrapper}>
            <div className={styles.brief}>
              <Typography
                variant="subtitle1"
                className={styles.textWrapper}
                sx={{ color: palette.gray.main }}
              >
                Ovde možete preuzeti dokument i detaljnije pročitati o našem programu
              </Typography>
              <div className={styles.briefBorder} />
              <div className={styles.briefFileWrapper}>
                <Typography variant="title2" sx={{ color: palette.blue100.main }}>
                  {bid.brief.filename}
                </Typography>
                <IconButton
                  sx={{
                    backgroundColor: palette.blue600.main,
                    width: '40px',
                    minWidth: '40px',
                    height: '40px',
                    padding: 0,
                  }}
                  onClick={() => {
                    void handleDownloadBrief().catch((error) => {
                      dispatch(setErrorMessage(error.message ?? 'Došlo je do greške'))
                    })
                  }}
                  icon={<DownloadIcon height="16px" width="16px" fill={palette.blue100.main} />}
                />
              </div>
            </div>
            <div className={styles.deadline}>
              <Typography variant="title8" sx={{ color: palette.charts.green100.main }}>
                {format(new Date(bid.deadlineProposal), 'dd.MM.yyyy')}
              </Typography>
              <Typography variant="title5" sx={{ color: palette.gray.main }}>
                Rok za davanje ponude
              </Typography>
            </div>
          </div>
        </div>
        <div className={styles.topicsWrapper}>
          <div className={styles.header}>
            <Typography variant="title1">Oblasti rada za ovaj projekat</Typography>
            <div className={styles.tenderNumWrapper}>
              <Typography variant="title3" sx={{ color: palette.gray.main }}>
                Broj tendera
              </Typography>
              <div className={styles.tenderNum}>
                <Typography variant="title3">{bid.tenderNum}</Typography>
              </div>
            </div>
          </div>
          <div className={styles.border} />
          <div className={styles.topics}>
            {bid.topics.map((bt, index) => (
              <div
                key={index}
                className={styles.topic}
                style={{
                  backgroundImage: `url(${projectDetailsBackground})`,
                  backgroundPosition: 'right bottom',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'auto 100%',
                  borderColor: getTopicColor(bt.type),
                  color: getTopicColor(bt.type),
                }}
              >
                <div className={styles.topicType}>
                  {getTopicIcon(bt.type)}
                  <Typography variant="title1">{getTopicLabel(bt.type)}</Typography>
                </div>
                <SimpleTooltip title={bt.name ?? '-'} style={{ textAlign: 'left' }}>
                  <Typography variant="title3" className={styles.topicName}>
                    {bt.name}
                  </Typography>
                </SimpleTooltip>
                <SimpleTooltip title={bt.categoryName ?? '-'} style={{ textAlign: 'left' }}>
                  <Typography
                    variant="subtitle1"
                    className={styles.topicCategoryName}
                    sx={{ color: palette.gray.main }}
                  >
                    {bt.categoryName}
                  </Typography>
                </SimpleTooltip>
              </div>
            ))}
          </div>
        </div>
      </Box>
    </Dialog>
  )
}

export default BidDetailsDialog
